import { reactive }            from 'vue';
import StateEventManager       from "@/js/Shared/Events/Managers/StateEventManager";
import AccountController       from "@/js/Webmail/AccountController";
import NotificationsController from "@/js/Webmail/Notifications/NotificationsController";
import KidsDataItem            from "./Entities/KidsDataItem";
import KidsHTTPMapping         from "./HTTP/KidsHTTPMapping";
import KidsHTTPService         from "./HTTP/KidsHTTPService";

export {KidsControllerSingleton as default};

/**
 * @description Controller responsible for managing
 * Kids account data
 *
 * @author rsommerfeld <roni.s.sommerfeld@altice.pt>
 *
 * @since 7.0.0
 *
 * @private
 */
class KidsController {

  /**
   * Kids Entity
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {KidsDataItem}
   */
  data = new KidsDataItem;

  /**
   * Kids HTTP Service
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {KidsHTTPService}
   */
  http_service = new KidsHTTPService();

  /**
   * Kids HTTP Mapping
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {KidsHTTPMapping}
   */
  http_mapping = new KidsHTTPMapping();

  /**
   * Kids Event State
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {StateEventManager}
   */
  state = new StateEventManager();

   /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  constructor() {
    this.setDataEntity(AccountController.account.kids);
  }

   /**
   * Check whether email or phone number is account valid
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} account_alias
   *
   * @return {Promise<Boolean>}
   */
  async submitAlias(account_alias) {
    this.state.setLoading('submit_alias');
    let response = await this.http_service.submitAlias(account_alias);
    this.state.setSuccess('submit_alias', response.success);

    if (response.success === false) {
      NotificationsController.addMessage('notifications.', response.messages);
    }

    if(response.success === true){
      this.http_mapping.mapSubmitAlias(this.data, response);
    }

    return response.success;
  }

  /**
   * Check whether secret and account are account valid
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} account_alias
   *
   * @return {Promise<Boolean>}
   */
  async submitSecret(account_alias, secret_alias) {
    this.state.setLoading('submit_secret');
    let response = await this.http_service.submitSecret(
      account_alias,
      secret_alias,
      this.data.auth_parent_method
    );

    if (response.success === false) {
      NotificationsController.addMessage('notifications.', response.messages);
    }

    this.http_mapping.mapParentAuth(this.data, response);
    this.state.setSuccess('submit_secret', response.success);

    return response.success;
  }

  /**
   * Fetches all kids definitions from the backend server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async fetchAllDefinitions(){
    this.state.setLoading('fetch_all_definitions');
    let response = await this.http_service.getAllDefinitions();
    this.state.setSuccess('fetch_all_definitions', response.success);

    if(response.success){
      this.http_mapping.mapKidsPrefs(this.data, response);
      this.http_mapping.mapKidsWhiteList(this.data, response.results.whitelist);
    }

    return response.success;
  }

  /**
   * Update kids definitions in backend server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} action
   * @param {Number} active
   *
   * @return {Promise<Boolean>}
   */
  async updateDefinitions(action, active){
    this.state.setLoading('update_definitions');
    let response = await this.http_service.updateDefinitions(action, active);
    this.state.setSuccess('update_definitions', response.success);

    NotificationsController.addMessage('notifications.', response.messages);

    return response.success;
  }

  /**
   * Adds an address from whitelist from kids definitions
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} address
   *
   * @return {Promise<Boolean>}
   */
  async addToWhitelist(address) {
    if (this.data.whitelist.exists(address)) {
      NotificationsController.addWarning('notifications.kids_prefs_whitelist_dup', [address]);
      return;
    }


    this.state.setLoading('saving_whitelist_email');
    let response = await this.http_service.addToWhitelist(address);
    this.state.setSuccess('saving_whitelist_email', response.success);

    if(response.success){
      this.http_mapping.mapKidsWhiteList(this.data, response.results);
    }

    NotificationsController.addMessage('notifications.', response.messages, [address]);
    return response.success;
  }

  /**
   * Remove email from whitelist kids
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} sender email to remove from whitelist
   *
   * @return {Promise<Boolean>}
   */
  async removeWhitelistSender(sender) {
    this.state.set(sender, 'loading');
    let response = await this.http_service.removeFromWhitelist(sender);

    if(response.success){
      this.http_mapping.mapRemoveWhitelist(this.data, response)
    }

    NotificationsController.addMessage('notifications.', response.messages, [sender]);
    this.state.unset(sender);

    return response.success;
  }

  /**
   * Maps Kid data from AccountController
   *
   * @param {Object} kids_data
   *
   * @private
   *
   * @since 7.0.0
   *
   * @returns {void}
   */
  setDataEntity(kids_data) {
    this.data.is_kid_account       = kids_data.is_kid;
    this.data.parent_authenticated = kids_data.parent_auth;
  }
}

const KidsControllerSingleton = reactive(new KidsController());